// src/components/MobileTabbedPanels.jsx
import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  Card,
  CardContent,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  alpha,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import MobileAnalyticsTable from './MobileAnalyticsTable';
import MobileCashflowTable from './MobileCashflowTable';
import MobileAdcoTable from './MobileAdcoTable';
import { CSVLink } from 'react-csv';
import dayjs from 'dayjs';

// Self-contained Mobile Price Calculator with local state for instant responsiveness
// State is kept fully local - only synced to parent on Generate click
const MobilePriceCalculator = memo(({
  tranches,
  priceTrancheTargets,
  onPriceTrancheTargetsChange,
  yieldTrancheTargets,
  onYieldTrancheTargetsChange,
  spreadTrancheTargets,
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets,
  onOwnPctTrancheTargetsChange,
  equityTranches = {},
  onGenerate,
  generationLoading,
  generateDisabled,
  hasGeneratedData,
  generationError,
  adcoApplied,
  theme
}) => {
  // Local state for immediate UI updates - fully controlled locally
  const [localPrices, setLocalPrices] = useState({});
  const [localYields, setLocalYields] = useState({});
  const [localSpreads, setLocalSpreads] = useState({});
  const [localOwnPcts, setLocalOwnPcts] = useState({});
  const initializedRef = useRef(false);
  
  // Only sync from external state on initial mount or when cleared (all empty)
  useEffect(() => {
    const allEmpty = !priceTrancheTargets || Object.keys(priceTrancheTargets).length === 0 ||
      Object.values(priceTrancheTargets).every(v => v === '' || v === null || v === undefined);
    
    if (!initializedRef.current || allEmpty) {
      setLocalPrices(priceTrancheTargets || {});
      initializedRef.current = true;
    }
  }, [priceTrancheTargets]);
  
  useEffect(() => {
    const allEmpty = !yieldTrancheTargets || Object.keys(yieldTrancheTargets).length === 0 ||
      Object.values(yieldTrancheTargets).every(v => v === '' || v === null || v === undefined);
    
    if (allEmpty) {
      setLocalYields(yieldTrancheTargets || {});
    }
  }, [yieldTrancheTargets]);
  
  useEffect(() => {
    const allEmpty = !spreadTrancheTargets || Object.keys(spreadTrancheTargets).length === 0 ||
      Object.values(spreadTrancheTargets).every(v => v === '' || v === null || v === undefined);

    if (allEmpty) {
      setLocalSpreads(spreadTrancheTargets || {});
    }
  }, [spreadTrancheTargets]);

  useEffect(() => {
    const allEmpty = !ownPctTrancheTargets || Object.keys(ownPctTrancheTargets).length === 0 ||
      Object.values(ownPctTrancheTargets).every(v => v === '' || v === null || v === undefined);
    if (allEmpty) {
      setLocalOwnPcts(ownPctTrancheTargets || {});
    }
  }, [ownPctTrancheTargets]);

  // Helper to check if a value exists
  const hasValue = (val) => val !== '' && val !== null && val !== undefined;
  
  // Disable logic based on LOCAL state for instant feedback
  const isPriceDisabled = (tranche) => hasValue(localYields[tranche]) || hasValue(localSpreads[tranche]);
  const isYieldDisabled = (tranche) => hasValue(localPrices[tranche]) || hasValue(localSpreads[tranche]);
  const isSpreadDisabled = (tranche) => hasValue(localPrices[tranche]) || hasValue(localYields[tranche]);
  
  // Simple handler - just update local state, no parent sync during editing
  const handleInputChange = useCallback((setter, tranche, value) => {
    const numValue = value === '' ? '' : Number(value);
    setter(prev => ({ ...prev, [tranche]: numValue }));
  }, []);
  
  // Sync all local state to parent and trigger generate
  const handleGenerateClick = useCallback(() => {
    if (onPriceTrancheTargetsChange) onPriceTrancheTargetsChange(localPrices);
    if (onYieldTrancheTargetsChange) onYieldTrancheTargetsChange(localYields);
    if (onSpreadTrancheTargetsChange) onSpreadTrancheTargetsChange(localSpreads);
    if (onOwnPctTrancheTargetsChange) onOwnPctTrancheTargetsChange(localOwnPcts);

    setTimeout(() => {
      if (onGenerate) onGenerate();
    }, 50);
  }, [localPrices, localYields, localSpreads, localOwnPcts, onPriceTrancheTargetsChange, onYieldTrancheTargetsChange, onSpreadTrancheTargetsChange, onOwnPctTrancheTargetsChange, onGenerate]);

  return (
    <Box sx={{ 
      p: 1.5, 
      bgcolor: theme.palette.background.paper,
      borderRadius: 1,
      border: '1px solid',
      borderColor: theme.palette.divider
    }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
        Price Calculator
      </Typography>
      
      {/* Price Row */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
          Price
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tranches.map((tranche) => {
            const isDisabled = isPriceDisabled(tranche);
            return (
              <Box key={`price-${tranche}`} sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 80 }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.25 }}>{tranche}</Typography>
                <TextField
                  type="number"
                  placeholder="100.00"
                  disabled={isDisabled}
                  size="small"
                  value={localPrices[tranche] ?? ''}
                  onChange={(e) => handleInputChange(setLocalPrices, tranche, e.target.value)}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      bgcolor: isDisabled 
                        ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
                        : theme.palette.background.paper,
                      '& input': {
                        textAlign: 'center',
                        py: 0.75
                      },
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                        opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                      }
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Yield Row */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
          Yield %
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tranches.map((tranche) => {
            const isDisabled = isYieldDisabled(tranche);
            return (
              <Box key={`yield-${tranche}`} sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 80 }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.25 }}>{tranche}</Typography>
                <TextField
                  type="number"
                  placeholder="5.00"
                  disabled={isDisabled}
                  size="small"
                  value={localYields[tranche] ?? ''}
                  onChange={(e) => handleInputChange(setLocalYields, tranche, e.target.value)}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      bgcolor: isDisabled 
                        ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
                        : theme.palette.background.paper,
                      '& input': {
                        textAlign: 'center',
                        py: 0.75
                      },
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                        opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                      }
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Spread Row */}
      <Box>
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
          Spread (bps)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tranches.map((tranche) => {
            const isDisabled = isSpreadDisabled(tranche);
            return (
              <Box key={`spread-${tranche}`} sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 80 }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.25 }}>{tranche}</Typography>
                <TextField
                  type="number"
                  placeholder="500"
                  disabled={isDisabled}
                  size="small"
                  value={localSpreads[tranche] ?? ''}
                  onChange={(e) => handleInputChange(setLocalSpreads, tranche, e.target.value)}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      bgcolor: isDisabled 
                        ? (theme.palette.mode === 'dark' ? alpha(theme.palette.action.disabled, 0.1) : '#f5f5f5')
                        : theme.palette.background.paper,
                      '& input': {
                        textAlign: 'center',
                        py: 0.75
                      },
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                        opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                      }
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Own % Row — only shown when at least one tranche is equity */}
      {tranches.some(t => equityTranches[t]) && (
        <Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
            Own %
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tranches.filter(t => equityTranches[t]).map((tranche) => (
              <Box key={`own-${tranche}`} sx={{ flex: '1 1 calc(50% - 4px)', minWidth: 80 }}>
                <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary, mb: 0.25 }}>{tranche}</Typography>
                <TextField
                  type="number"
                  placeholder="100.00"
                  size="small"
                  value={localOwnPcts[tranche] ?? ''}
                  onChange={(e) => handleInputChange(setLocalOwnPcts, tranche, e.target.value)}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '12px',
                      bgcolor: theme.palette.background.paper,
                      '& input': { textAlign: 'center', py: 0.75 },
                      '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                        opacity: theme.palette.mode === 'dark' ? 1 : 0.6,
                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                      }
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Generate Button */}
      {onGenerate && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleGenerateClick}
            disabled={generateDisabled || generationLoading}
            fullWidth
            sx={{
              bgcolor: generationLoading 
                ? theme.palette.action.disabled 
                : theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
              py: 0.75,
              textTransform: 'none',
              fontSize: '12px',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled
              }
            }}
          >
            {generationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} sx={{ color: 'white' }} />
                Generating...
              </Box>
            ) : hasGeneratedData ? (
              'Re-Generate'
            ) : (
              'Generate'
            )}
          </Button>

          {adcoApplied && (
            <Chip
              label="ADCO curves applied"
              size="small"
              color="info"
              variant="outlined"
              sx={{ mt: 1, height: 20, fontSize: '10px' }}
            />
          )}

          {/* Error Message Display */}
          {generationError && (
            <TextField
              value={generationError}
              multiline
              rows={2}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              sx={{
                mt: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  borderColor: theme.palette.error.main,
                  '& fieldset': {
                    borderColor: theme.palette.error.main,
                  },
                  '& textarea': {
                    fontSize: '12px',
                    color: theme.palette.error.main,
                  }
                }
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
});

MobilePriceCalculator.displayName = 'MobilePriceCalculator';

const MobileTabbedPanels = ({
  tabValue,
  onTabChange,
  selectedRows,
  selectedTranches,
  adcoEnabled,
  // Analytics props
  analyticsData,
  analyticsLoading,
  panelAnalyticsTranches,
  onAnalyticsTranchesChange,
  // Price calculator props
  priceTrancheTargets,
  onPriceTrancheTargetsChange,
  yieldTrancheTargets,
  onYieldTrancheTargetsChange,
  spreadTrancheTargets,
  onSpreadTrancheTargetsChange,
  ownPctTrancheTargets,
  onOwnPctTrancheTargetsChange,
  equityTranches = {},
  priceData,
  // Generate action props
  onGenerate,
  generationLoading,
  generateDisabled,
  hasGeneratedData,
  generationError,
  // Cashflow props
  cashflowData,
  cashflowMonthlyData = [],
  cashflowGrain = 'year',
  onCashflowGrainChange = () => {},
  cashflowLoading,
  panelCashflowTranche,
  onCashflowTrancheChange,
  // ADCO props
  panelAdcoTranche,
  onAdcoTrancheChange,
  modifiedDate,
  adcoData,
  adcoApplied,
  onRunAdco,
  adcoLoading
}) => {
  const theme = useTheme();
  const hasAdco = Object.keys(adcoEnabled).length > 0 && Object.values(adcoEnabled).some(v => v);
  // Monthly rows (when already available from a generate/comparison response)
  // let the toggle switch instantly; the stored-data path refetches by grain
  // in the parent (ScenView).
  const activeCashflowData = cashflowGrain === 'month' ? cashflowMonthlyData : cashflowData;

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        mb: 9 // Space for bottom action bar (reduced for compact toolbar)
      }}
    >
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => onTabChange(newValue)}
        variant="fullWidth"
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: 600,
            fontSize: '13px',
            minHeight: 56,
            textTransform: 'none',
            '&.Mui-selected': {
              color: 'white',
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
            height: 3
          }
        }}
      >
        <Tab label="Analytics" />
        <Tab label="Cashflow" />
        {hasAdco && <Tab label="ADCO" />}
      </Tabs>

      {/* Tab Panels */}
      {tabValue === 0 && (
        <CardContent sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
          {/* Controls */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Tranches</InputLabel>
              <Select
                multiple
                value={panelAnalyticsTranches}
                onChange={(e) => onAnalyticsTranchesChange(e.target.value)}
                label="Select Tranches"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '11px' }} />
                    ))}
                  </Box>
                )}
              >
                {selectedTranches.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Price Calculator - Self-contained component with local state for instant responsiveness */}
            {panelAnalyticsTranches.length > 0 && (
              <MobilePriceCalculator
                adcoApplied={adcoApplied}
                tranches={panelAnalyticsTranches}
                priceTrancheTargets={priceTrancheTargets}
                onPriceTrancheTargetsChange={onPriceTrancheTargetsChange}
                yieldTrancheTargets={yieldTrancheTargets}
                onYieldTrancheTargetsChange={onYieldTrancheTargetsChange}
                spreadTrancheTargets={spreadTrancheTargets}
                onSpreadTrancheTargetsChange={onSpreadTrancheTargetsChange}
                ownPctTrancheTargets={ownPctTrancheTargets}
                onOwnPctTrancheTargetsChange={onOwnPctTrancheTargetsChange}
                equityTranches={equityTranches}
                onGenerate={onGenerate}
                generationLoading={generationLoading}
                generateDisabled={generateDisabled}
                hasGeneratedData={hasGeneratedData}
                generationError={generationError}
                theme={theme}
              />
            )}

            {analyticsData && analyticsData.length > 0 && (
              <CSVLink
                data={Array.isArray(analyticsData) ? analyticsData : []}
                filename={`analytics_${Date.now()}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </CSVLink>
            )}
          </Box>

          {analyticsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : generationError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography color="text.secondary" fontSize="13px">
                No analytics data available
              </Typography>
            </Box>
          ) : (
            <MobileAnalyticsTable
              data={Array.isArray(analyticsData) ? analyticsData : []}
            />
          )}
        </CardContent>
      )}

      {tabValue === 1 && (
        <CardContent sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
          {/* Controls */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Tranche</InputLabel>
              <Select
                value={panelCashflowTranche}
                onChange={(e) => onCashflowTrancheChange(e.target.value)}
                label="Select Tranche"
              >
                {selectedTranches.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={cashflowGrain}
              onChange={(_, value) => value && onCashflowGrainChange(value)}
              aria-label="Cashflow grain"
            >
              <ToggleButton value="month">Monthly</ToggleButton>
              <ToggleButton value="year">Yearly</ToggleButton>
            </ToggleButtonGroup>

            {activeCashflowData && activeCashflowData.length > 0 && (
              <CSVLink
                data={activeCashflowData.filter(row => {
                  const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
                  return !panelCashflowTranche || trancheValue === panelCashflowTranche;
                })}
                filename={`cashflow_${panelCashflowTranche}_${Date.now()}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </CSVLink>
            )}
          </Box>

          {cashflowLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <MobileCashflowTable
              data={activeCashflowData?.filter(row => {
                const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
                return !panelCashflowTranche || trancheValue === panelCashflowTranche;
              }) || []}
            />
          )}
        </CardContent>
      )}

      {tabValue === 2 && hasAdco && selectedRows.length > 0 && (
        <CardContent sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
          {/* Controls */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Tranche</InputLabel>
              <Select
                value={panelAdcoTranche}
                onChange={(e) => onAdcoTrancheChange(e.target.value)}
                label="Select Tranche"
              >
                {selectedTranches.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={onRunAdco}
              disabled={adcoLoading}
              fullWidth
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {adcoLoading ? 'Running ADCO...' : 'Run ADCO'}
            </Button>

            {adcoData && adcoData.length > 0 && (
              <CSVLink
                data={adcoData.filter(row => {
                  const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
                  return !panelAdcoTranche || trancheValue === panelAdcoTranche;
                })}
                filename={`adco_${panelAdcoTranche}_${Date.now()}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ textTransform: 'none' }}
                >
                  Export CSV
                </Button>
              </CSVLink>
            )}
          </Box>

          {adcoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <MobileAdcoTable
              data={adcoData?.filter(row => {
                const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
                return !panelAdcoTranche || trancheValue === panelAdcoTranche;
              }) || []}
              condensedView={true}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default MobileTabbedPanels;

